import {
  useCallback,
  useEffect,
  useState,
  type FormEvent
} from "react";
import type {
  AssetView,
  PromptTemplateView,
  SharedLibraryItemView
} from "@seedance/contracts";
import { apiClient, type ApiClient, type UserRole } from "../api/client.js";
import { libraryCopy } from "./library-copy.js";
import { AssetThumbnail } from "./AssetThumbnail.js";

export type ProductionLibraryTab = "assets" | "templates" | "shared" | "trash";
export type ProductionLibraryStatus = "loading" | "ready" | "error";

export interface ProductionLibraryDialogViewProps {
  tab: ProductionLibraryTab;
  status: ProductionLibraryStatus;
  assets: AssetView[];
  templates: PromptTemplateView[];
  sharedItems: SharedLibraryItemView[];
  notice: string | undefined;
  viewerRole?: UserRole;
  onClose(): void;
  onSelectTab(tab: ProductionLibraryTab): void;
  onImport(item: SharedLibraryItemView): void;
  onWithdraw(item: SharedLibraryItemView): void;
  onTrashAsset(asset: AssetView): void;
  onRestoreAsset(asset: AssetView): void;
  onTrashTemplate(template: PromptTemplateView): void;
  onRestoreTemplate(template: PromptTemplateView): void;
  onUpdateTemplate?(
    template: PromptTemplateView,
    input: { title: string; content: string }
  ): void;
  onPublishAsset(asset: AssetView): void;
  onPublishTemplate(template: PromptTemplateView): void;
  publishedAssetIds?: ReadonlySet<string>;
  onAddAsset?: ((asset: AssetView) => void) | undefined;
  onApplyTemplate?: ((template: PromptTemplateView) => void) | undefined;
  onRetry?(): void;
  onCreateTemplate?(input: { title: string; content: string }): void;
  onSearchShared?(query: string): void;
}

const tabs: Array<{ value: ProductionLibraryTab; label: string }> = [
  { value: "assets", label: libraryCopy.assets },
  { value: "templates", label: libraryCopy.templates },
  { value: "shared", label: libraryCopy.shared },
  { value: "trash", label: libraryCopy.trash }
];

function mediaLabel(kind: "image" | "video" | "audio"): string {
  return kind === "image"
    ? libraryCopy.image
    : kind === "video"
      ? libraryCopy.video
      : libraryCopy.audio;
}

function EmptyState({ tab }: { tab: ProductionLibraryTab }) {
  const message =
    tab === "assets"
      ? libraryCopy.emptyAssets
      : tab === "templates"
        ? libraryCopy.emptyTemplates
        : tab === "shared"
          ? libraryCopy.emptyShared
          : libraryCopy.emptyTrash;
  return <p className="production-library__empty">{message}</p>;
}

function AssetCard({
  asset,
  trash,
  onAdd,
  onTrash,
  onRestore,
  onPublish,
  published
}: {
  asset: AssetView;
  trash: boolean;
  onAdd?: ((asset: AssetView) => void) | undefined;
  onTrash(asset: AssetView): void;
  onRestore(asset: AssetView): void;
  onPublish(asset: AssetView): void;
  published: boolean;
}) {
  return (
    <li className="production-library-card">
      {asset.kind === "audio" ? (
        <span className="production-library-card__media" aria-hidden="true">♫</span>
      ) : (
        <AssetThumbnail assetId={asset.id} />
      )}
      <div className="production-library-card__body">
        <strong title={asset.displayName}>{asset.displayName}</strong>
        <small>{mediaLabel(asset.kind)}</small>
      </div>
      <div className="production-library-card__actions">
        {trash ? (
          <button type="button" onClick={() => onRestore(asset)}>{libraryCopy.restore}</button>
        ) : (
          <>
            {onAdd ? <button type="button" onClick={() => onAdd(asset)}>{libraryCopy.addToCanvas}</button> : null}
            {published ? (
              <span className="production-library-card__shared-status">已共享到团队</span>
            ) : (
              <button type="button" onClick={() => onPublish(asset)}>{libraryCopy.publish}</button>
            )}
            <button type="button" onClick={() => onTrash(asset)}>{libraryCopy.trashAction}</button>
          </>
        )}
      </div>
    </li>
  );
}

function TemplateCard({
  template,
  trash,
  onApply,
  onTrash,
  onRestore,
  onPublish,
  onUpdate
}: {
  template: PromptTemplateView;
  trash: boolean;
  onApply?: ((template: PromptTemplateView) => void) | undefined;
  onTrash(template: PromptTemplateView): void;
  onRestore(template: PromptTemplateView): void;
  onPublish(template: PromptTemplateView): void;
  onUpdate?: ((
    template: PromptTemplateView,
    input: { title: string; content: string }
  ) => void) | undefined;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(template.title);
  const [content, setContent] = useState(template.content);
  const save = (event: FormEvent) => {
    event.preventDefault();
    onUpdate?.(template, { title: title.trim(), content: content.trim() });
    setEditing(false);
  };
  return (
    <li className="production-library-card production-library-card--prompt">
      <div className="production-library-card__body">
        {editing ? (
          <form className="production-library-card__edit-form" onSubmit={save}>
            <input
              aria-label={libraryCopy.templateTitle}
              value={title}
              required
              onChange={(event) => setTitle(event.currentTarget.value)}
            />
            <textarea
              aria-label={libraryCopy.templateContent}
              value={content}
              required
              onChange={(event) => setContent(event.currentTarget.value)}
            />
            <div>
              <button type="submit">{libraryCopy.saveTemplateChanges}</button>
              <button type="button" onClick={() => {
                setTitle(template.title);
                setContent(template.content);
                setEditing(false);
              }}>{libraryCopy.cancelEdit}</button>
            </div>
          </form>
        ) : (
          <>
            <strong>{template.title}</strong>
            <p>{template.content}</p>
          </>
        )}
      </div>
      {!editing ? <div className="production-library-card__actions">
        {trash ? (
          <button type="button" onClick={() => onRestore(template)}>{libraryCopy.restore}</button>
        ) : (
          <>
            {onApply ? <button type="button" onClick={() => onApply(template)}>{libraryCopy.applyToSelectedSeedance}</button> : null}
            {onUpdate ? <button type="button" onClick={() => setEditing(true)}>{libraryCopy.editTemplate}</button> : null}
            <button type="button" onClick={() => onPublish(template)}>{libraryCopy.publish}</button>
            <button type="button" onClick={() => onTrash(template)}>{libraryCopy.trashAction}</button>
          </>
        )}
      </div> : null}
    </li>
  );
}

function SharedCard({
  item,
  viewerRole,
  onImport,
  onWithdraw
}: {
  item: SharedLibraryItemView;
  viewerRole?: UserRole;
  onImport(item: SharedLibraryItemView): void;
  onWithdraw(item: SharedLibraryItemView): void;
}) {
  return (
    <li className="production-library-card production-library-card--shared">
      <span className="production-library-card__media" aria-hidden="true">
        {item.kind === "prompt" ? "文" : mediaLabel(item.media.kind).slice(0, 1)}
      </span>
      <div className="production-library-card__body">
        <strong>{item.title}</strong>
        {item.description ? <p>{item.description}</p> : null}
        <small>{libraryCopy.author}：{item.authorName}</small>
        {item.kind === "prompt" ? <p>{item.promptContent}</p> : null}
      </div>
      <div className="production-library-card__actions">
        <button type="button" onClick={() => onImport(item)}>
          {item.kind === "asset" ? libraryCopy.importAsset : libraryCopy.importPrompt}
        </button>
        {item.canWithdraw ? (
          <button type="button" onClick={() => onWithdraw(item)}>
            {viewerRole === "system_admin"
              ? libraryCopy.administratorWithdraw
              : libraryCopy.withdraw}
          </button>
        ) : null}
      </div>
    </li>
  );
}

export function ProductionLibraryDialogView(
  props: ProductionLibraryDialogViewProps
) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [sharedQuery, setSharedQuery] = useState("");
  const createTemplate = (event: FormEvent) => {
    event.preventDefault();
    props.onCreateTemplate?.({ title, content });
  };
  const searchShared = (event: FormEvent) => {
    event.preventDefault();
    props.onSearchShared?.(sharedQuery);
  };
  const trashAssets = props.assets.filter((asset) => asset.trashedAt !== null);
  const trashTemplates = props.templates.filter(
    (template) => template.trashedAt !== null
  );
  const publishedAssetIds = props.publishedAssetIds ?? new Set<string>();
  const hasItems =
    props.tab === "assets"
      ? props.assets.length > 0
      : props.tab === "templates"
        ? props.templates.length > 0
        : props.tab === "shared"
          ? props.sharedItems.length > 0
          : trashAssets.length + trashTemplates.length > 0;

  return (
    <div className="production-library-backdrop">
      <section
        className="production-library"
        role="dialog"
        aria-modal="true"
        aria-labelledby="production-library-title"
      >
        <header>
          <div>
            <h2 id="production-library-title">{libraryCopy.title}</h2>
            <p>{libraryCopy.subtitle}</p>
          </div>
          <button type="button" aria-label={libraryCopy.close} onClick={props.onClose}>×</button>
        </header>
        <nav role="tablist" aria-label={libraryCopy.title}>
          {tabs.map((item) => (
            <button
              key={item.value}
              type="button"
              role="tab"
              aria-selected={props.tab === item.value}
              onClick={() => props.onSelectTab(item.value)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {props.tab === "templates" && props.onCreateTemplate ? (
          <form className="production-library__template-form" onSubmit={createTemplate}>
            <input
              aria-label={libraryCopy.templateTitle}
              placeholder={libraryCopy.templateTitle}
              value={title}
              onChange={(event) => setTitle(event.currentTarget.value)}
            />
            <textarea
              aria-label={libraryCopy.templateContent}
              placeholder={libraryCopy.templateContent}
              value={content}
              onChange={(event) => setContent(event.currentTarget.value)}
            />
            <button type="submit">{libraryCopy.saveTemplate}</button>
          </form>
        ) : null}
        {props.tab === "shared" && props.onSearchShared ? (
          <form className="production-library__search" onSubmit={searchShared}>
            <input
              aria-label={libraryCopy.searchShared}
              placeholder={libraryCopy.searchShared}
              value={sharedQuery}
              onChange={(event) => setSharedQuery(event.currentTarget.value)}
            />
            <button type="submit">{libraryCopy.search}</button>
          </form>
        ) : null}

        {props.notice ? (
          <p
            className="production-library__notice"
            role={props.status === "error" ? "alert" : "status"}
            aria-live="polite"
          >
            {props.notice}
            {props.status === "error" && props.onRetry ? (
              <button type="button" onClick={props.onRetry}>{libraryCopy.retry}</button>
            ) : null}
          </p>
        ) : null}
        {props.status === "loading" ? (
          <p className="production-library__empty" aria-live="polite">{libraryCopy.loading}</p>
        ) : null}
        {props.status === "ready" && !hasItems ? <EmptyState tab={props.tab} /> : null}

        {props.status === "ready" && hasItems ? (
          <ul className="production-library__grid">
            {props.tab === "assets"
              ? props.assets.map((asset) => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    trash={false}
                    onAdd={props.onAddAsset}
                    onTrash={props.onTrashAsset}
                    onRestore={props.onRestoreAsset}
                    onPublish={props.onPublishAsset}
                    published={publishedAssetIds.has(asset.id)}
                  />
                ))
              : null}
            {props.tab === "templates"
              ? props.templates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    trash={false}
                    onApply={props.onApplyTemplate}
                    onTrash={props.onTrashTemplate}
                    onRestore={props.onRestoreTemplate}
                    onPublish={props.onPublishTemplate}
                    onUpdate={props.onUpdateTemplate}
                  />
                ))
              : null}
            {props.tab === "shared"
              ? props.sharedItems.map((item) => (
                  <SharedCard
                    key={item.id}
                    item={item}
                    {...(props.viewerRole ? { viewerRole: props.viewerRole } : {})}
                    onImport={props.onImport}
                    onWithdraw={props.onWithdraw}
                  />
                ))
              : null}
            {props.tab === "trash"
              ? [
                  ...trashAssets.map((asset) => (
                    <AssetCard
                      key={`asset-${asset.id}`}
                      asset={asset}
                      trash
                      onAdd={undefined}
                      onTrash={props.onTrashAsset}
                      onRestore={props.onRestoreAsset}
                      onPublish={props.onPublishAsset}
                      published={false}
                    />
                  )),
                  ...trashTemplates.map((template) => (
                    <TemplateCard
                      key={`template-${template.id}`}
                      template={template}
                      trash
                      onApply={undefined}
                      onTrash={props.onTrashTemplate}
                      onRestore={props.onRestoreTemplate}
                      onPublish={props.onPublishTemplate}
                      onUpdate={undefined}
                    />
                  ))
                ]
              : null}
          </ul>
        ) : null}
      </section>
    </div>
  );
}

export interface ProductionLibraryDialogProps {
  open: boolean;
  initialTab?: ProductionLibraryTab;
  onClose(): void;
  onAssetImported(asset: AssetView): void;
  onPromptImported(template: PromptTemplateView): void;
  onAddAsset?: ((asset: AssetView) => void) | undefined;
  onApplyTemplate?: ((template: PromptTemplateView) => void) | undefined;
  viewerRole?: UserRole;
  client?: ApiClient;
}

export function ProductionLibraryDialog({
  open,
  initialTab = "assets",
  onClose,
  onAssetImported,
  onPromptImported,
  onAddAsset,
  onApplyTemplate,
  viewerRole,
  client = apiClient
}: ProductionLibraryDialogProps) {
  const [tab, setTab] = useState<ProductionLibraryTab>(initialTab);
  const [status, setStatus] = useState<ProductionLibraryStatus>("loading");
  const [assets, setAssets] = useState<AssetView[]>([]);
  const [templates, setTemplates] = useState<PromptTemplateView[]>([]);
  const [sharedItems, setSharedItems] = useState<SharedLibraryItemView[]>([]);
  const [notice, setNotice] = useState<string>();
  const [sharedQuery, setSharedQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setNotice(undefined);
  }, [initialTab, open]);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose, open]);

  const loadTab = useCallback(async (requestedTab: ProductionLibraryTab) => {
    if (!open) return;
    setStatus("loading");
    try {
      if (requestedTab === "assets") {
        const [assetResponse, sharedResponse] = await Promise.all([
          client.listAssets({ view: "library", limit: 100 }),
          client.listSharedLibrary({ limit: 100 })
        ]);
        setAssets(assetResponse.assets);
        setSharedItems(sharedResponse.items);
      } else if (requestedTab === "templates") {
        setTemplates(await client.listPromptTemplates("active"));
      } else if (requestedTab === "shared") {
        const response = await client.listSharedLibrary({
          ...(sharedQuery ? { q: sharedQuery } : {}),
          limit: 100
        });
        setSharedItems(response.items);
      } else {
        const [assetResponse, templateResponse] = await Promise.all([
          client.listAssets({ view: "trash", limit: 100 }),
          client.listPromptTemplates("trash")
        ]);
        setAssets(assetResponse.assets);
        setTemplates(templateResponse);
      }
      setStatus("ready");
    } catch {
      setStatus("error");
      setNotice(libraryCopy.loadFailed);
    }
  }, [client, open, sharedQuery]);

  const load = useCallback(async () => {
    await loadTab(tab);
  }, [loadTab, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (
    operation: () => Promise<void>,
    success: string,
    nextTab?: ProductionLibraryTab
  ) => {
    setNotice(undefined);
    try {
      await operation();
      const targetTab = nextTab ?? tab;
      await loadTab(targetTab);
      if (nextTab) setTab(nextTab);
      setNotice(success);
    } catch {
      setNotice(libraryCopy.operationFailed);
    }
  };

  if (!open) return null;
  return (
    <ProductionLibraryDialogView
      tab={tab}
      status={status}
      assets={assets}
      templates={templates}
      sharedItems={sharedItems}
      publishedAssetIds={new Set(
        sharedItems.flatMap((item) =>
          item.kind === "asset" && item.sourceAssetId ? [item.sourceAssetId] : []
        )
      )}
      notice={notice}
      {...(viewerRole ? { viewerRole } : {})}
      onClose={onClose}
      onSelectTab={(nextTab) => {
        setNotice(undefined);
        setTab(nextTab);
      }}
      onRetry={() => void load()}
      onSearchShared={(query) => setSharedQuery(query.trim())}
      onCreateTemplate={(input) => {
        if (!input.title.trim() || !input.content.trim()) {
          setNotice(libraryCopy.templateRequired);
          return;
        }
        void run(
          async () => {
            await client.createPromptTemplate(input);
          },
          libraryCopy.saveTemplate
        );
      }}
      onImport={(item) => {
        void run(async () => {
          const imported = await client.importSharedItem(item.id);
          if (imported.kind === "asset") onAssetImported(imported.asset);
          else onPromptImported(imported.template);
        }, item.kind === "asset" ? libraryCopy.importedAsset : libraryCopy.importedPrompt,
        item.kind === "asset" ? "assets" : "templates");
      }}
      onWithdraw={(item) => {
        void run(async () => {
          await client.withdrawSharedItem(item.id);
        }, libraryCopy.withdrawn);
      }}
      onTrashAsset={(asset) => {
        void run(async () => {
          await client.trashAsset(asset.id);
        }, libraryCopy.movedToTrash);
      }}
      onRestoreAsset={(asset) => {
        void run(async () => {
          await client.restoreAsset(asset.id);
        }, libraryCopy.restored);
      }}
      onTrashTemplate={(template) => {
        void run(async () => {
          await client.trashPromptTemplate(template.id);
        }, libraryCopy.movedToTrash);
      }}
      onRestoreTemplate={(template) => {
        void run(async () => {
          await client.restorePromptTemplate(template.id);
        }, libraryCopy.restored);
      }}
      onUpdateTemplate={(template, input) => {
        if (!input.title || !input.content) {
          setNotice(libraryCopy.templateRequired);
          return;
        }
        void run(async () => {
          await client.updatePromptTemplate(template.id, input);
        }, "模板已更新。");
      }}
      onPublishAsset={(asset) => {
        void run(async () => {
          await client.publishSharedAsset({
            assetId: asset.id,
            title: asset.displayName,
            description: ""
          });
        }, libraryCopy.published);
      }}
      onAddAsset={onAddAsset}
      onApplyTemplate={onApplyTemplate}
      onPublishTemplate={(template) => {
        void run(async () => {
          await client.publishSharedPrompt({
            templateId: template.id,
            description: ""
          });
        }, libraryCopy.published);
      }}
    />
  );
}
